/* eslint-disable arrow-body-style */
import { defineStore } from 'pinia'
import {
  computed, ref, toRefs, watch,
} from 'vue'

import {
  requestCreateLikeFeed,
  requestDeleteLikeFeed,
} from '@/api/feeds'

import useGetFeedsData from '../composables/useGetFeedsData'
import useTweetData from '../composables/useTweetData'
import useCommentData from '../composables/useCommentData'
import * as FEED_LIST_CATEGORY from '../constants/feeds_list_categories'
import * as FEEDABLE_TYPES from '../constants/feedable_types'

const useFeedsStore = defineStore('feeds', () => {
  const feedlistCategory = ref(null)
  const stockId = ref(null)

  const _feedableTypesByListCategory = computed(() => {
    switch (feedlistCategory.value) {
    case FEED_LIST_CATEGORY.ALL: // 用於我的追蹤頁
      return FEEDABLE_TYPES.ALL
    case FEED_LIST_CATEGORY.ALL_FOR_ANALYSIS: // 用於個股頁
      return FEEDABLE_TYPES.ALL_FOR_ANALYSIS
    case FEED_LIST_CATEGORY.ANNOUNCEMENT:
      return [
        FEEDABLE_TYPES.ANNOUNCEMENT,
        FEEDABLE_TYPES.SEC_FILING,
        FEEDABLE_TYPES.EARNINGS_CALL,
      ]
    case FEED_LIST_CATEGORY.NEWS:
      return [
        FEEDABLE_TYPES.YAHOO_STOCK_FEED,
        FEEDABLE_TYPES.US_YAHOO_NEWS,
        FEEDABLE_TYPES.SEEKING_ALPHA_FEED,
      ]
    default:
      return FEEDABLE_TYPES.ALL_FOR_ANALYSIS
    }
  })

  const requestParams = computed(() => {
    const params = {
      feedable_types: _feedableTypesByListCategory.value,
    }

    if (stockId.value !== null) {
      params.stock_id = stockId.value
    }

    return params
  })

  const { feedsData, getFeedsData, clearFeedsData } = useGetFeedsData({
    ticker: null,
    requestParams,
    onMountedFetch: false,
  })
  // state setter
  const setStockId = ({ id }) => {
    stockId.value = id
  }
  const setFeedlistCategory = ({ category }) => {
    feedlistCategory.value = category
  }

  // private function
  const _deleteFeed = ({ feedableId }) => {
    feedsData.items = feedsData.items.filter((feed) => feed.feedable_id !== feedableId)
  }
  const _updateFeed = ({ feed, attributes }) => {
    const feedIndex = feedsData.items.indexOf(feed)

    if (feedIndex !== -1) {
      feedsData.items.splice(feedIndex, 1, {
        ...feed,
        ...attributes,
      })
    }
  }
  const _findFeed = ({ feedId = null, feedableId = null }) => feedsData.items.find((feed) => {
    if (feedId) {
      return feed.feed_id === feedId
    }
    if (feedableId) {
      return feed.feedable_id === feedableId
    }
    return false
  })

  // Feed
  const getFeedList = () => {
    return getFeedsData().catch((error) => {
      if (error.response && error.response.status === 429) {
        return
      }

      if (error.code !== 'ERR_NETWORK') {
        throw error
      }
    })
  }

  const toggleFeedLike = ({ feed }) => {
    const newLikersCount = (() => {
      if (feed.liked) {
        return feed.likers_count - 1
      }

      return feed.likers_count + 1
    })()

    _updateFeed({
      feed,
      attributes: {
        liked: !feed.liked,
        likers_count: newLikersCount,
      },
    })

    if (feed.liked) {
      return requestDeleteLikeFeed({ feedId: feed.feed_id })
    }

    return requestCreateLikeFeed({ feedId: feed.feed_id })
  }

  // Tweet
  const createTweet = ({ content }) => {
    return useTweetData().createTweet({ stockId: stockId.value, content })
      .then(({ data }) => {
        feedsData.items = [data, ...feedsData.items]
      })
  }
  const deleteTweet = ({ feed }) => {
    useTweetData().deleteTweet({ feedableId: feed.feedable_id })
      .then(() => {
        _deleteFeed({ feedableId: feed.feedable_id })
      })
  }

  // Comment
  const createComment = ({ feed, content }) => {
    return useCommentData().createComment({ feedId: feed.feed_id, content })
      .then(({ data }) => {
        _updateFeed({
          feed,
          attributes: {
            comments: [...feed.comments, data.comment],
          },
        })
      })
  }
  const deleteComment = ({ comment }) => {
    const targetFeed = _findFeed({ feedId: comment.feed_id })
    useCommentData().deleteComment({ feedId: comment.feed_id, commentId: comment.id })
      .then(() => {
        _updateFeed({
          feed: targetFeed,
          attributes: {
            comments: targetFeed.comments.filter((commentItem) => commentItem.id !== comment.id),
          },
        })
      })
  }
  const getMoreComments = ({ feed, nextUrl }) => {
    useCommentData().getCommentList({ nextUrl })
      .then(({ data: { comments, comments_pagination: pagination } }) => {
        _updateFeed({
          feed,
          attributes: {
            comments: [...comments, ...feed.comments],
            comments_pagination: pagination,
          },
        })
      })
  }
  const toggleCommentLike = ({ comment }) => {
    const newLikersCount = (() => {
      if (comment.liked) {
        return comment.likers_count - 1
      }

      return comment.likers_count + 1
    })()
    const targetFeed = _findFeed({ feedId: comment.feed_id })
    useCommentData().toggleCommentLike({ comment })
      .then(() => {
        _updateFeed({
          feed: targetFeed,
          attributes: {
            comments: targetFeed.comments.map((commentItem) => {
              if (commentItem.id === comment.id) {
                return {
                  ...commentItem,
                  liked: !commentItem.liked,
                  likers_count: newLikersCount,
                }
              }
              return commentItem
            }),
          },
        })
      })
  }

  // Watch
  watch(
    requestParams,
    () => {
      clearFeedsData()
      getFeedList()
    },
  )

  return {
    feedlistCategory,
    setFeedlistCategory,
    setStockId,

    ...toRefs(feedsData),
    clearFeedsData,
    getFeedList,
    toggleFeedLike,

    createTweet,
    deleteTweet,
    resetTweetData: useTweetData().resetTweetData,
    isCreatingTweet: useTweetData().tweetData.isCreating,
    hasCreatingTweetError: useTweetData().tweetData.hasCreatingError,

    createComment,
    deleteComment,
    getMoreComments,
    toggleCommentLike,
    isCreatingComment: useCommentData().commentData.isCreating,
  }
})

export default useFeedsStore
